import { useEffect, useRef, useState } from 'react'
import { BotaoIcone } from './BotaoIcone'
import { IconeMicrofone, IconeParar, IconeX } from './icones'
import { juntar } from './ui'

/**
 * Gravar e mandar áudio, como no WhatsApp.
 *
 * Áudio é metade da conversa de venda por WhatsApp — explicar uma dúvida
 * falando leva vinte segundos e escrevendo leva cinco
 * minutos. Sem isto, o vendedor gravava no celular e mandava por fora, e a
 * conversa do CRM ficava com um buraco.
 *
 * Grava com `MediaRecorder`, que todo navegador de desktop atual tem. O
 * formato sai como o navegador entrega — webm/opus no Chrome — e é o mesmo
 * que a Evolution aceita para áudio.
 *
 * A permissão do microfone é pedida no primeiro clique, pelo próprio
 * navegador. Negada, o botão explica em vez de simplesmente não funcionar.
 */

type Estado = 'parado' | 'gravando' | 'sem-permissao'

export function GravadorDeAudio({
  aoGravar,
  desabilitado,
}: {
  aoGravar: (audio: File) => void
  desabilitado?: boolean
}) {
  const [estado, setEstado] = useState<Estado>('parado')
  const [segundos, setSegundos] = useState(0)
  const gravador = useRef<MediaRecorder | null>(null)
  const pedacos = useRef<Blob[]>([])
  const cancelado = useRef(false)

  useEffect(() => {
    if (estado !== 'gravando') return
    const t = setInterval(() => setSegundos((s) => s + 1), 1000)
    return () => clearInterval(t)
  }, [estado])

  /*
    Solta o microfone ao desmontar.

    Sem isto, trocar de conversa no meio de uma gravação deixava a luzinha do
    microfone acesa e a captura viva — o navegador só libera quando as trilhas
    param.
  */
  useEffect(() => {
    return () => {
      gravador.current?.stream.getTracks().forEach((t) => t.stop())
    }
  }, [])

  async function comecar() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream)
      pedacos.current = []
      cancelado.current = false

      mr.ondataavailable = (e) => {
        if (e.data.size > 0) pedacos.current.push(e.data)
      }

      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop())
        setEstado('parado')
        setSegundos(0)
        if (cancelado.current) return

        const blob = new Blob(pedacos.current, { type: mr.mimeType || 'audio/webm' })
        // Áudio vazio acontece quando o clique de parar vem antes do primeiro
        // pedaço: mandar um arquivo de zero byte só gera erro do outro lado.
        if (blob.size === 0) return

        const extensao = (mr.mimeType || 'audio/webm').includes('ogg') ? 'ogg' : 'webm'
        aoGravar(new File([blob], `audio-${Date.now()}.${extensao}`, { type: blob.type }))
      }

      gravador.current = mr
      mr.start()
      setEstado('gravando')
      setSegundos(0)
    } catch {
      setEstado('sem-permissao')
    }
  }

  function parar(descartar: boolean) {
    cancelado.current = descartar
    gravador.current?.stop()
  }

  if (estado === 'sem-permissao') {
    return (
      <BotaoIcone
        rotulo="Microfone bloqueado no navegador. Libere nas permissões do site"
        dicaAlinhada="direita"
        onClick={() => setEstado('parado')}
        className="text-amber-600"
      >
        <IconeMicrofone className="h-5 w-5" />
      </BotaoIcone>
    )
  }

  if (estado === 'gravando') {
    return (
      <span className="flex items-center gap-1 rounded-md bg-red-50 px-2 py-1">
        <span className="h-2 w-2 animate-pulse rounded-full bg-red-600" />
        <span className="w-10 text-xs tabular-nums text-red-700">{formatar(segundos)}</span>

        <BotaoIcone rotulo="Descartar" onClick={() => parar(true)}>
          <IconeX className="h-4 w-4" />
        </BotaoIcone>
        {/*
          Parar manda; o X descarta. São dois botões e não um porque desistir
          de um áudio é tão comum quanto mandá-lo, e um clique errado no
          mesmo botão significaria enviar o que se queria jogar fora.
        */}
        <BotaoIcone
          rotulo="Parar e enviar"
          destaque
          dicaAlinhada="direita"
          onClick={() => parar(false)}
        >
          <IconeParar className="h-4 w-4" />
        </BotaoIcone>
      </span>
    )
  }

  return (
    <BotaoIcone
      rotulo="Gravar áudio"
      dicaAlinhada="direita"
      disabled={desabilitado}
      onClick={comecar}
      className={juntar('text-tinta-600')}
    >
      <IconeMicrofone className="h-5 w-5" />
    </BotaoIcone>
  )
}

function formatar(segundos: number): string {
  const m = Math.floor(segundos / 60)
  const s = segundos % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
